import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Stack,
  Text,
  Pivot,
  PivotItem,
  Dropdown,
  IDropdownOption,
  MessageBar,
  MessageBarType,
  Toggle,
  IconButton,
} from "@fluentui/react";
import { DataGrid, DataGridColumn } from "@/ui-controls/DataGrid";
import AddButton from "@/ui-controls/AddButton";
import SearchBox from "@/ui-controls/SearchBox";
import MoreActionsButton from "@/ui-controls/MoreActionsButton";
import ShimmerList from "@/ui-controls/ShimmerList";
import RigthPanel, { RigthPanelType } from "@/ui-controls/RigthPanel";
import SaveButton from "@/ui-controls/SaveButton";
import CancelButton from "@/ui-controls/CancelButton";
import TextInput from "@/ui-controls/TextInput";
import CustomerComboBox from "@/ui-controls/CustomerComboBox";
import PricingFilterPanel, {
  PricingFilterOptions,
  defaultPricingFilters,
} from "@/components/integrations/pricing/PricingFilterPanel";
import { Grid } from "fluentui-react-grid";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  IntegrationDto,
  IntegrationDefaultPricingDto,
  CustomerIntegrationPricingDto,
  listIntegrations,
  listDefaultPricing,
  createDefaultPricing,
  updateDefaultPricing,
  listCustomerPricing,
  setupCustomerPricing,
} from "@/api/integrations";
import { Customer, getAllActiveCustomers } from "@/api/customers";

// ── Default Pricing Form ──

const DefaultPricingForm = (props: {
  integrationId?: number;
  integrations?: IntegrationDto[];
  pricing?: IntegrationDefaultPricingDto | null;
  onSave: () => void;
  onClose: () => void;
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const integrationOptions: IDropdownOption[] = (props.integrations || []).map(
    (i) => ({ key: i.id, text: `${i.code} - ${i.name}` })
  );

  const formik = useFormik({
    initialValues: {
      integrationId: props.integrationId ?? props.pricing?.integrationId ?? 0,
      currencyCode: props.pricing?.currencyCode ?? "USD",
      pricePerUse: props.pricing?.pricePerUse?.toString() ?? "",
      includedUses: props.pricing?.includedUses?.toString() ?? "0",
      effectiveFrom: props.pricing?.effectiveFrom
        ? props.pricing.effectiveFrom.split("T")[0]
        : new Date().toISOString().split("T")[0],
      effectiveTo: props.pricing?.effectiveTo
        ? props.pricing.effectiveTo.split("T")[0]
        : "",
      isActive: props.pricing?.isActive ?? true,
    },
    validationSchema: Yup.object({
      integrationId: Yup.number()
        .required("Integration is required")
        .positive("Integration is required"),
      currencyCode: Yup.string().required("Currency is required").max(3),
      pricePerUse: Yup.number().required("Price per use is required").min(0),
      includedUses: Yup.number().required("Included uses is required").min(0),
      effectiveFrom: Yup.string().required("Effective from is required"),
    }),
    onSubmit: (values) => {
      setIsSaving(true);
      setErrorMessage("");
      const isEdit = !!props.pricing;
      const action = isEdit
        ? updateDefaultPricing({
            id: props.pricing!.id,
            integrationId: values.integrationId,
            currencyCode: values.currencyCode,
            pricePerUse: Number(values.pricePerUse),
            includedUses: Number(values.includedUses),
            effectiveFrom: values.effectiveFrom,
            effectiveTo: values.effectiveTo || null,
            isActive: values.isActive,
          })
        : createDefaultPricing({
            integrationId: values.integrationId,
            currencyCode: values.currencyCode,
            pricePerUse: Number(values.pricePerUse),
            includedUses: Number(values.includedUses),
            effectiveFrom: values.effectiveFrom,
            effectiveTo: values.effectiveTo || null,
            isActive: values.isActive,
          });
      action
        .then(
          () => props.onSave?.(),
          (error) => {
            const data = error?.response?.data;
            const msg = data?.detail
              || data?.title
              || (typeof data === "string" ? data : null)
              || JSON.stringify(data?.errors)
              || "Failed to save pricing.";
            setErrorMessage(msg);
          }
        )
        .finally(() => setIsSaving(false));
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid dir="ltr">
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <MessageBar messageBarType={MessageBarType.info}>
              Define the base pricing for an integration. The <strong>Included Uses</strong> sets
              how many free transactions are allowed within the effective period. Any usage beyond
              that allowance is charged at the <strong>Price Per Use</strong> rate.
            </MessageBar>
          </Grid.Col>
        </Grid.Row>
        {errorMessage && (
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <MessageBar
                messageBarType={MessageBarType.error}
                onDismiss={() => setErrorMessage("")}
              >
                {errorMessage}
              </MessageBar>
            </Grid.Col>
          </Grid.Row>
        )}
        {!props.integrationId && (
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <Dropdown
                label="Integration"
                placeholder="Select an integration"
                options={integrationOptions}
                selectedKey={formik.values.integrationId || undefined}
                onChange={(_, option) =>
                  formik.setFieldValue("integrationId", option?.key ?? 0)
                }
                disabled={isSaving || !!props.pricing}
                errorMessage={
                  formik.errors.integrationId && formik.touched.integrationId
                    ? formik.errors.integrationId
                    : undefined
                }
              />
            </Grid.Col>
          </Grid.Row>
        )}
        <Grid.Row>
          <Grid.Col sizeSm={4}>
            <Dropdown
              label="Currency"
              options={[{ key: "USD", text: "USD" }]}
              selectedKey={formik.values.currencyCode || undefined}
              onChange={(_, option) =>
                formik.setFieldValue("currencyCode", option?.key ?? "")
              }
              disabled={isSaving}
              errorMessage={
                formik.errors.currencyCode && formik.touched.currencyCode
                  ? formik.errors.currencyCode
                  : undefined
              }
            />
          </Grid.Col>
          <Grid.Col sizeSm={4}>
            <TextInput
              label="Price Per Use"
              baseTypeProps={{
                disabled: isSaving,
                inputMode: "decimal",
                ...formik.getFieldProps("pricePerUse"),
              }}
              hasError={formik.errors.pricePerUse && formik.touched.pricePerUse}
              errorMessage={formik.errors.pricePerUse}
            />
          </Grid.Col>
          <Grid.Col sizeSm={4}>
            <TextInput
              label="Included Uses"
              baseTypeProps={{
                disabled: isSaving,
                type: "number",
                ...formik.getFieldProps("includedUses"),
              }}
              hasError={
                formik.errors.includedUses && formik.touched.includedUses
              }
              errorMessage={formik.errors.includedUses}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={6}>
            <TextInput
              label="Effective From"
              baseTypeProps={{
                disabled: isSaving,
                type: "date",
                ...formik.getFieldProps("effectiveFrom"),
              }}
              hasError={
                formik.errors.effectiveFrom && formik.touched.effectiveFrom
              }
              errorMessage={formik.errors.effectiveFrom}
            />
          </Grid.Col>
          <Grid.Col sizeSm={6}>
            <TextInput
              label="Effective To"
              baseTypeProps={{
                disabled: isSaving,
                type: "date",
                ...formik.getFieldProps("effectiveTo"),
              }}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <Toggle
              label="Active"
              checked={formik.values.isActive}
              onChange={(_, checked) =>
                formik.setFieldValue("isActive", checked ?? false)
              }
              disabled={isSaving}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row className="panel-buttons">
          <Grid.Col>
            <SaveButton
              text="Save"
              baseTypeProps={{ type: "submit", disabled: isSaving }}
            />
          </Grid.Col>
          <Grid.Col>
            <CancelButton
              text="Cancel"
              onClick={props.onClose}
              baseTypeProps={{ disabled: isSaving }}
            />
          </Grid.Col>
        </Grid.Row>
      </Grid>
    </form>
  );
};

// ── Customer Pricing Form ──

const CustomerPricingForm = (props: {
  integrationId?: number;
  integrations?: IntegrationDto[];
  pricing?: CustomerIntegrationPricingDto | null;
  onSave: () => void;
  onClose: () => void;
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const isEdit = !!props.pricing;

  const integrationOptions: IDropdownOption[] = (props.integrations || []).map(
    (i) => ({ key: i.id, text: `${i.code} - ${i.name}` })
  );

  const formik = useFormik({
    initialValues: {
      integrationId: props.integrationId ?? props.pricing?.integrationId ?? 0,
      customerId: props.pricing?.customerId?.toString() ?? "",
      currencyCode: props.pricing?.currencyCode ?? "USD",
      pricePerUse: props.pricing?.pricePerUse?.toString() ?? "",
      includedUses: props.pricing?.includedUses?.toString() ?? "0",
      effectiveFrom: props.pricing?.effectiveFrom
        ? props.pricing.effectiveFrom.split("T")[0]
        : new Date().toISOString().split("T")[0],
      effectiveTo: props.pricing?.effectiveTo
        ? props.pricing.effectiveTo.split("T")[0]
        : "",
      isActive: props.pricing?.isActive ?? true,
    },
    validationSchema: Yup.object({
      integrationId: Yup.number()
        .required("Integration is required")
        .positive("Integration is required"),
      customerId: Yup.number()
        .required("Customer is required")
        .positive(),
      currencyCode: Yup.string().required("Currency is required").max(3),
      pricePerUse: Yup.number().required("Price per use is required").min(0),
      includedUses: Yup.number().required("Included uses is required").min(0),
      effectiveFrom: Yup.string().required("Effective from is required"),
    }),
    onSubmit: (values) => {
      setIsSaving(true);
      setErrorMessage("");
      setupCustomerPricing({
        integrationId: values.integrationId,
        customerId: Number(values.customerId),
        currencyCode: values.currencyCode,
        pricePerUse: Number(values.pricePerUse),
        includedUses: Number(values.includedUses),
        effectiveFrom: values.effectiveFrom,
        effectiveTo: values.effectiveTo || null,
        isActive: values.isActive,
      })
        .then(
          () => props.onSave?.(),
          (error) => {
            const data = error?.response?.data;
            setErrorMessage(data?.detail || "Failed to save pricing.");
          }
        )
        .finally(() => setIsSaving(false));
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid dir="ltr">
        {errorMessage && (
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <MessageBar
                messageBarType={MessageBarType.error}
                onDismiss={() => setErrorMessage("")}
              >
                {errorMessage}
              </MessageBar>
            </Grid.Col>
          </Grid.Row>
        )}
        {!props.integrationId && !isEdit && (
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <Dropdown
                label="Integration"
                placeholder="Select an integration"
                options={integrationOptions}
                selectedKey={formik.values.integrationId || undefined}
                onChange={(_, option) =>
                  formik.setFieldValue("integrationId", option?.key ?? 0)
                }
                disabled={isSaving}
                errorMessage={
                  formik.errors.integrationId && formik.touched.integrationId
                    ? formik.errors.integrationId
                    : undefined
                }
              />
            </Grid.Col>
          </Grid.Row>
        )}
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <CustomerComboBox
              selectedCustomerId={
                formik.values.customerId
                  ? Number(formik.values.customerId)
                  : undefined
              }
              onChange={(id) =>
                formik.setFieldValue("customerId", id ? String(id) : "")
              }
              disabled={isSaving || isEdit}
              errorMessage={
                formik.errors.customerId && formik.touched.customerId
                  ? formik.errors.customerId
                  : undefined
              }
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={4}>
            <Dropdown
              label="Currency"
              options={[{ key: "USD", text: "USD" }]}
              selectedKey={formik.values.currencyCode || undefined}
              onChange={(_, option) =>
                formik.setFieldValue("currencyCode", option?.key ?? "")
              }
              disabled={isSaving}
              errorMessage={
                formik.errors.currencyCode && formik.touched.currencyCode
                  ? formik.errors.currencyCode
                  : undefined
              }
            />
          </Grid.Col>
          <Grid.Col sizeSm={4}>
            <TextInput
              label="Price Per Use"
              baseTypeProps={{
                disabled: isSaving,
                inputMode: "decimal",
                ...formik.getFieldProps("pricePerUse"),
              }}
              hasError={formik.errors.pricePerUse && formik.touched.pricePerUse}
              errorMessage={formik.errors.pricePerUse}
            />
          </Grid.Col>
          <Grid.Col sizeSm={4}>
            <TextInput
              label="Included Uses"
              baseTypeProps={{
                disabled: isSaving,
                type: "number",
                ...formik.getFieldProps("includedUses"),
              }}
              hasError={
                formik.errors.includedUses && formik.touched.includedUses
              }
              errorMessage={formik.errors.includedUses}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={6}>
            <TextInput
              label="Effective From"
              baseTypeProps={{
                disabled: isSaving,
                type: "date",
                ...formik.getFieldProps("effectiveFrom"),
              }}
              hasError={
                formik.errors.effectiveFrom && formik.touched.effectiveFrom
              }
              errorMessage={formik.errors.effectiveFrom}
            />
          </Grid.Col>
          <Grid.Col sizeSm={6}>
            <TextInput
              label="Effective To"
              baseTypeProps={{
                disabled: isSaving,
                type: "date",
                ...formik.getFieldProps("effectiveTo"),
              }}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <Toggle
              label="Active"
              checked={formik.values.isActive}
              onChange={(_, checked) =>
                formik.setFieldValue("isActive", checked ?? false)
              }
              disabled={isSaving}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row className="panel-buttons">
          <Grid.Col>
            <SaveButton
              text="Save"
              baseTypeProps={{ type: "submit", disabled: isSaving }}
            />
          </Grid.Col>
          <Grid.Col>
            <CancelButton
              text="Cancel"
              onClick={props.onClose}
              baseTypeProps={{ disabled: isSaving }}
            />
          </Grid.Col>
        </Grid.Row>
      </Grid>
    </form>
  );
};

// ── Main Page ──

const IntegrationPricingPage = () => {
  const [integrations, setIntegrations] = useState<IntegrationDto[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [defaultPricing, setDefaultPricing] = useState<
    IntegrationDefaultPricingDto[]
  >([]);
  const [customerPricing, setCustomerPricing] = useState<
    CustomerIntegrationPricingDto[]
  >([]);
  const [loadingDefault, setLoadingDefault] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(false);

  // Search & Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilters, setSelectedFilters] =
    useState<PricingFilterOptions>({ ...defaultPricingFilters });
  const [appliedFilters, setAppliedFilters] =
    useState<PricingFilterOptions>({ ...defaultPricingFilters });
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const [isNewDefaultOpen, setIsNewDefaultOpen] = useState(false);
  const [isEditDefaultOpen, setIsEditDefaultOpen] = useState(false);
  const [selectedDefault, setSelectedDefault] =
    useState<IntegrationDefaultPricingDto | null>(null);
  const [isNewCustomerOpen, setIsNewCustomerOpen] = useState(false);
  const [isEditCustomerOpen, setIsEditCustomerOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerIntegrationPricingDto | null>(null);

  const loadData = useCallback(() => {
    setLoadingDefault(true);
    setLoadingCustomer(true);

    Promise.all([
      listIntegrations(undefined, 1000),
      getAllActiveCustomers(),
      listDefaultPricing(undefined, undefined, 1000),
      listCustomerPricing(undefined, undefined, undefined, 1000),
    ])
      .then(([intData, custData, defData, custPricingData]) => {
        setIntegrations(intData?.result || []);
        setCustomers(custData || []);
        setDefaultPricing(defData?.result || []);
        setCustomerPricing(custPricingData?.result || []);
      })
      .catch(() => {
        setIntegrations([]);
        setCustomers([]);
        setDefaultPricing([]);
        setCustomerPricing([]);
      })
      .finally(() => {
        setLoadingDefault(false);
        setLoadingCustomer(false);
      });
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getIntegrationName = (integrationId: number) => {
    const integration = integrations.find((i) => i.id === integrationId);
    return integration
      ? `${integration.code} - ${integration.name}`
      : integrationId.toString();
  };

  const getCustomerName = (customerId: number | null) => {
    if (customerId == null) return "-";
    const customer = customers.find((c) => c.Id === customerId);
    return customer
      ? `${customer.Name} (${customerId})`
      : customerId.toString();
  };

  // Filtered default pricing
  const filteredDefault = useMemo(() => {
    let items = defaultPricing;

    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      items = items.filter((p) => {
        const intName = getIntegrationName(p.integrationId).toLowerCase();
        return (
          intName.includes(lower) ||
          p.currencyCode.toLowerCase().includes(lower)
        );
      });
    }

    if (appliedFilters.statusFilter === "active") {
      items = items.filter((p) => p.isActive);
    } else if (appliedFilters.statusFilter === "inactive") {
      items = items.filter((p) => !p.isActive);
    }

    if (appliedFilters.integrationId !== undefined) {
      items = items.filter(
        (p) => p.integrationId === appliedFilters.integrationId
      );
    }

    return items;
  }, [defaultPricing, searchTerm, appliedFilters, integrations]);

  // Filtered customer pricing
  const filteredCustomer = useMemo(() => {
    let items = customerPricing;

    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      items = items.filter((p) => {
        const intName = getIntegrationName(p.integrationId).toLowerCase();
        const custName = getCustomerName(p.customerId).toLowerCase();
        return (
          intName.includes(lower) ||
          custName.includes(lower) ||
          p.currencyCode.toLowerCase().includes(lower)
        );
      });
    }

    if (appliedFilters.statusFilter === "active") {
      items = items.filter((p) => p.isActive);
    } else if (appliedFilters.statusFilter === "inactive") {
      items = items.filter((p) => !p.isActive);
    }

    if (appliedFilters.integrationId !== undefined) {
      items = items.filter(
        (p) => p.integrationId === appliedFilters.integrationId
      );
    }

    if (appliedFilters.customerId !== undefined) {
      items = items.filter((p) => p.customerId === appliedFilters.customerId);
    }

    return items;
  }, [customerPricing, searchTerm, appliedFilters, integrations, customers]);

  const hasFiltersApplied =
    appliedFilters.statusFilter !== "all" ||
    appliedFilters.integrationId !== undefined ||
    appliedFilters.customerId !== undefined;

  const loading = loadingDefault || loadingCustomer;

  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
  };

  const handleRefresh = () => {
    setSearchTerm("");
    setSelectedFilters({ ...defaultPricingFilters });
    setAppliedFilters({ ...defaultPricingFilters });
    loadData();
  };

  const defaultColumns: DataGridColumn[] = [
    {
      key: "integration",
      name: "Integration",
      minWidth: 140,
      maxWidth: 220,
      onRender: (item: IntegrationDefaultPricingDto) =>
        getIntegrationName(item.integrationId),
    },
    {
      key: "currencyCode",
      name: "Currency",
      fieldName: "currencyCode",
      minWidth: 80,
      maxWidth: 100,
    },
    {
      key: "pricePerUse",
      name: "Price/Use",
      fieldName: "pricePerUse",
      minWidth: 80,
      maxWidth: 100,
    },
    {
      key: "includedUses",
      name: "Included Uses",
      fieldName: "includedUses",
      minWidth: 100,
      maxWidth: 120,
    },
    {
      key: "effectiveFrom",
      name: "From",
      fieldName: "effectiveFrom",
      minWidth: 100,
      maxWidth: 120,
    },
    {
      key: "effectiveTo",
      name: "To",
      minWidth: 100,
      maxWidth: 120,
      onRender: (item: IntegrationDefaultPricingDto) =>
        item.effectiveTo || "-",
    },
    {
      key: "isActive",
      name: "Status",
      minWidth: 80,
      maxWidth: 100,
      onRender: (item: IntegrationDefaultPricingDto) => (
        <Text
          styles={{
            root: {
              color: item.isActive ? "#107c10" : "#a80000",
              fontWeight: 600,
            },
          }}
        >
          {item.isActive ? "Active" : "Inactive"}
        </Text>
      ),
    },
    {
      key: "actions",
      name: "Actions",
      minWidth: 80,
      maxWidth: 80,
      onRender: (item: IntegrationDefaultPricingDto) => (
        <MoreActionsButton
          menu={[
            {
              key: "edit",
              text: "Edit",
              iconProps: { iconName: "Edit" },
              onClick: () => {
                setSelectedDefault(item);
                setIsEditDefaultOpen(true);
              },
            },
          ]}
        />
      ),
    },
  ];

  const customerColumns: DataGridColumn[] = [
    {
      key: "integration",
      name: "Integration",
      minWidth: 140,
      maxWidth: 220,
      onRender: (item: CustomerIntegrationPricingDto) =>
        getIntegrationName(item.integrationId),
    },
    {
      key: "customerId",
      name: "Customer",
      minWidth: 120,
      maxWidth: 200,
      onRender: (item: CustomerIntegrationPricingDto) =>
        getCustomerName(item.customerId),
    },
    {
      key: "currencyCode",
      name: "Currency",
      fieldName: "currencyCode",
      minWidth: 80,
      maxWidth: 100,
    },
    {
      key: "pricePerUse",
      name: "Price/Use",
      fieldName: "pricePerUse",
      minWidth: 80,
      maxWidth: 100,
    },
    {
      key: "includedUses",
      name: "Included Uses",
      fieldName: "includedUses",
      minWidth: 100,
      maxWidth: 120,
    },
    {
      key: "effectiveFrom",
      name: "From",
      fieldName: "effectiveFrom",
      minWidth: 100,
      maxWidth: 120,
    },
    {
      key: "effectiveTo",
      name: "To",
      minWidth: 100,
      maxWidth: 120,
      onRender: (item: CustomerIntegrationPricingDto) =>
        item.effectiveTo || "-",
    },
    {
      key: "isActive",
      name: "Status",
      minWidth: 80,
      maxWidth: 100,
      onRender: (item: CustomerIntegrationPricingDto) => (
        <Text
          styles={{
            root: {
              color: item.isActive ? "#107c10" : "#a80000",
              fontWeight: 600,
            },
          }}
        >
          {item.isActive ? "Active" : "Inactive"}
        </Text>
      ),
    },
    {
      key: "actions",
      name: "Actions",
      minWidth: 80,
      maxWidth: 80,
      onRender: (item: CustomerIntegrationPricingDto) => (
        <MoreActionsButton
          menu={[
            {
              key: "edit",
              text: "Edit",
              iconProps: { iconName: "Edit" },
              onClick: () => {
                setSelectedCustomer(item);
                setIsEditCustomerOpen(true);
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb-left-section">
          <div>
            <h2 className="page-breadcrumb-title">Integration Pricing</h2>
            <div style={{ color: "#605e5c", fontSize: "14px" }}>
              Manage default and customer-specific pricing
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <AddButton
            text="New Default Pricing"
            onClick={() => setIsNewDefaultOpen(true)}
          />
          <SearchBox
            placeholder="Search pricing..."
            onSearch={handleSearch}
            disabled={loading}
          />
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <IconButton
              iconProps={{ iconName: "Filter" }}
              title="Filter pricing"
              ariaLabel="Filter pricing"
              onClick={() => setIsFilterPanelOpen(true)}
              disabled={loading}
              styles={{
                root: {
                  width: 40,
                  height: 40,
                  backgroundColor: hasFiltersApplied
                    ? "#deecf9"
                    : "transparent",
                },
                rootHovered: { backgroundColor: "#f3f2f1" },
                icon: {
                  fontSize: 18,
                  color: loading ? "#a19f9d" : "#0078d4",
                },
                rootPressed: { backgroundColor: "#e1dfdd" },
                rootDisabled: { backgroundColor: "transparent" },
              }}
            />
            {hasFiltersApplied && (
              <div
                style={{
                  position: "absolute",
                  top: "-2px",
                  right: "-2px",
                  backgroundColor: "#d13438",
                  color: "white",
                  borderRadius: "50%",
                  width: "16px",
                  height: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: "600",
                  zIndex: 1,
                }}
              >
                1
              </div>
            )}
          </div>
          <IconButton
            iconProps={{ iconName: "Refresh" }}
            title="Refresh"
            ariaLabel="Refresh"
            onClick={handleRefresh}
            disabled={loading}
            styles={{
              root: { width: 40, height: 40 },
              rootHovered: { backgroundColor: "#f3f2f1" },
              icon: {
                fontSize: 18,
                color: loading ? "#a19f9d" : "#0078d4",
              },
              rootPressed: { backgroundColor: "#e1dfdd" },
              rootDisabled: { backgroundColor: "transparent" },
            }}
          />
        </div>
      </div>

      <PricingFilterPanel
        isOpen={isFilterPanelOpen}
        onDismiss={() => setIsFilterPanelOpen(false)}
        selectedFilters={selectedFilters}
        onFiltersChange={setSelectedFilters}
        onApplyFilters={setAppliedFilters}
        onClearFilters={() => {
          setSelectedFilters({ ...defaultPricingFilters });
          setAppliedFilters({ ...defaultPricingFilters });
        }}
      />

      <div style={{ padding: "16px" }}>
        <Pivot>
          <PivotItem headerText="Default Pricing">
            <Stack
              tokens={{ childrenGap: 12 }}
              styles={{ root: { marginTop: 12 } }}
            >
              {loadingDefault ? (
                <ShimmerList numberOfColumns={7} />
              ) : (
                <DataGrid items={filteredDefault} columns={defaultColumns} />
              )}
            </Stack>
          </PivotItem>
          <PivotItem headerText="Customer Pricing">
            <Stack
              tokens={{ childrenGap: 12 }}
              styles={{ root: { marginTop: 12 } }}
            >
              <Stack horizontal horizontalAlign="end">
                <AddButton
                  text="New Customer Pricing"
                  onClick={() => setIsNewCustomerOpen(true)}
                />
              </Stack>
              {loadingCustomer ? (
                <ShimmerList numberOfColumns={9} />
              ) : (
                <DataGrid
                  items={filteredCustomer}
                  columns={customerColumns}
                />
              )}
            </Stack>
          </PivotItem>
        </Pivot>
      </div>

      <RigthPanel
        header="New Default Pricing"
        isOpen={isNewDefaultOpen}
        type={RigthPanelType.medium}
        dismissPanel={() => setIsNewDefaultOpen(false)}
        childComponent={DefaultPricingForm}
        childProps={{
          integrations,
          onSave: () => {
            setIsNewDefaultOpen(false);
            loadData();
          },
          onClose: () => setIsNewDefaultOpen(false),
        }}
      />
      {selectedDefault && (
        <RigthPanel
          header="Edit Default Pricing"
          isOpen={isEditDefaultOpen}
          type={RigthPanelType.medium}
          dismissPanel={() => {
            setIsEditDefaultOpen(false);
            setSelectedDefault(null);
          }}
          childComponent={DefaultPricingForm}
          childProps={{
            integrations,
            pricing: selectedDefault,
            onSave: () => {
              setIsEditDefaultOpen(false);
              setSelectedDefault(null);
              loadData();
            },
            onClose: () => {
              setIsEditDefaultOpen(false);
              setSelectedDefault(null);
            },
          }}
        />
      )}
      <RigthPanel
        header="New Customer Pricing"
        isOpen={isNewCustomerOpen}
        type={RigthPanelType.small}
        dismissPanel={() => setIsNewCustomerOpen(false)}
        childComponent={CustomerPricingForm}
        childProps={{
          integrations,
          onSave: () => {
            setIsNewCustomerOpen(false);
            loadData();
          },
          onClose: () => setIsNewCustomerOpen(false),
        }}
      />
      {selectedCustomer && (
        <RigthPanel
          header="Edit Customer Pricing"
          isOpen={isEditCustomerOpen}
          type={RigthPanelType.small}
          dismissPanel={() => {
            setIsEditCustomerOpen(false);
            setSelectedCustomer(null);
          }}
          childComponent={CustomerPricingForm}
          childProps={{
            integrationId: selectedCustomer.integrationId,
            pricing: selectedCustomer,
            onSave: () => {
              setIsEditCustomerOpen(false);
              setSelectedCustomer(null);
              loadData();
            },
            onClose: () => {
              setIsEditCustomerOpen(false);
              setSelectedCustomer(null);
            },
          }}
        />
      )}
    </>
  );
};

export default IntegrationPricingPage;
